import { PULL_TEST_BULK_CREATE_MAX_ROWS } from './pullTestingBulkCreate';
import type {
  CreatePullTestingModuleRequest,
  PullTestingInstallationCapture,
  PullTestingInstallationField,
  PullTestingModule,
  PullTestPiling,
  PullTestPlan,
  PullTestPlanStep,
  PullTestResultsStep,
} from './project';
import {
  normalizePullTestingInstallationFields,
  pullTestInstallationFieldBlueprint,
  PULL_TEST_INSTALLATION_FIELD_ORDER,
  resolvePullTestingInstallationFields,
} from './project';

/**
 * H-pile section suggestions for the Load cases step (`<datalist>`).
 * Screw / Helical use free-form only.
 */
export const PULL_TEST_WIZARD_H_PILE_SECTION_PRESETS: readonly string[] = [
  'W6x7',
  'W6x8.5',
  'W6x9',
  'W6x12',
  'W6x15',
  'W8x10',
  'W8x13',
  'W8x15',
  'W8x18',
];

/** One editable row in the Test models step table (maps to `PullTestPlanStep`). */
export interface WizardPlanStepDraft {
  holdTimeSec: number;
  targetLoadLbs: number;
}

/** Per-type template from wizard step “Test models”. */
export interface PullTestingWizardTestTemplate {
  enabled: boolean;
  /** Optional display name for this schedule (mock: model name). */
  scheduleName?: string;
  maxLoadLbs: number;
  incrementLoadLbs: number;
  holdTimeSec: number;
  /** Lateral: 2 or 3 deflection value columns in results; tensile/compression always use 2. */
  deflectionCount: 2 | 3;
  /** Lateral field setup (stored on `lateralTestResults`, not each plan step). */
  lateralLoadHeightAboveGradeFt?: number;
  lateralGaugeHeightIn?: number;
  /** Lateral plan: second gauge height (ft), applied uniformly to every plan step when present. */
  lateralSecondDeflectionHeightFt?: number | null;
  /** Max deflection (in) before failure — saved on the test plan; not part of the step grid. */
  deflectionFailureCriteriaIn?: number | null;
  /**
   * Explicit load-hold schedule. When missing/empty, steps are derived from
   * `maxLoadLbs` / `incrementLoadLbs` / `holdTimeSec` (same as pre-mockup wizard).
   */
  customSteps?: WizardPlanStepDraft[] | null;
  /** Mock parity: multiple named models can be defined per test type. */
  models?: PullTestingWizardNamedModel[] | null;
  /** Active model id mirrored into scalar fields for compatibility/building. */
  activeModelId?: string | null;
}

/** One named load model under a test type (mock `loadModels[type][]`). */
export interface PullTestingWizardNamedModel {
  id: string;
  name: string;
  maxLoadLbs: number;
  incrementLoadLbs: number;
  holdTimeSec: number;
  /** Lateral: 2 or 3; tensile/compression are always 2. */
  deflectionCount: 2 | 3;
  lateralLoadHeightAboveGradeFt?: number;
  lateralGaugeHeightIn?: number;
  /** Second deflection gauge height (ft); copied onto {@link PullTestPlan.lateralSecondDeflectionHeightFt} when building the plan. */
  lateralSecondDeflectionHeightFt?: number | null;
  customSteps?: WizardPlanStepDraft[] | null;
  /** Failure threshold (in) — same as template field; persisted on {@link PullTestPlan}. */
  deflectionFailureCriteriaIn?: number | null;
}

export interface PullTestingWizardLoadCaseRow {
  /** Wizard stores template rows with `0`; not used in `buildBulkPilingRows` (it loops all locations). */
  locationIndex: number;
  slotIndex: number;
  pileIdentifier: string;
  pileLengthFt?: number | null;
  /** Default embedment for all sites; Review can override per location. `null` = empty in the form until filled. */
  embedmentDepthFt: number | null;
  /** Soak time shared for all sites for this slot. `null` = empty until filled. */
  soakTimeHours: number | null;
  pileType: string;
  pileCoating?: string;
  pileSection?: string;
  preExcavDepthFt?: number | null;
  preDrillDiameterIn?: number | null;
  preDrillDepthFt?: number | null;
  enableTensile: boolean;
  enableLateral: boolean;
  enableCompression: boolean;
  /** Review step: assigned named model id per test type (mock `pilingAssignments`). */
  tensileModelId?: string | null;
  lateralModelId?: string | null;
  compressionModelId?: string | null;
}

export interface PullTestingWizardPilingOverride {
  embedmentDepthFt?: number | null;
  /** Suffix after `{prefix}-{locationSerial}` (same as load-case `pileIdentifier`); overrides template for this location. */
  pileIdentifier?: string | null;
  preDrillDiameterIn?: number | null;
  preDrillDepthFt?: number | null;
  tensileModelId?: string | null;
  lateralModelId?: string | null;
  compressionModelId?: string | null;
}

/** In-memory draft for the Pull Testing setup wizard (mobile / web). */
export interface PullTestingWizardDraft {
  pilePrefix: string;
  locationSerialStart: number;
  numLocations: number;
  pilingsPerLocation: number;
  installation: PullTestingInstallationCapture;
  tensile: PullTestingWizardTestTemplate;
  lateral: PullTestingWizardTestTemplate;
  compression: PullTestingWizardTestTemplate;
  loadCases: PullTestingWizardLoadCaseRow[];
}

const defaultInstallationFields = (): PullTestingInstallationField[] =>
  PULL_TEST_INSTALLATION_FIELD_ORDER.map(k => pullTestInstallationFieldBlueprint(k));

export const defaultPullTestingInstallationCapture = (): PullTestingInstallationCapture =>
  defaultInstallationFields();

export const defaultWizardTestTemplate = (): PullTestingWizardTestTemplate => ({
  enabled: false,
  scheduleName: '',
  maxLoadLbs: 1000,
  incrementLoadLbs: 100,
  holdTimeSec: 60,
  deflectionCount: 2,
  lateralLoadHeightAboveGradeFt: 0,
  lateralGaugeHeightIn: 0,
  lateralSecondDeflectionHeightFt: null,
  deflectionFailureCriteriaIn: null,
  customSteps: null,
  models: null,
  activeModelId: null,
});

let wizardModelUid = 0;
const nextWizardModelId = (): string => `wm-${++wizardModelUid}`;

export const defaultWizardNamedModel = (name = 'Model'): PullTestingWizardNamedModel => ({
  id: nextWizardModelId(),
  name,
  maxLoadLbs: 1000,
  incrementLoadLbs: 100,
  holdTimeSec: 60,
  deflectionCount: 2,
  lateralLoadHeightAboveGradeFt: 0,
  lateralGaugeHeightIn: 0,
  lateralSecondDeflectionHeightFt: null,
  customSteps: null,
  deflectionFailureCriteriaIn: null,
});

/** Build default step rows from max / increment / hold (mock `generateLMRows` semantics). */
export function wizardStepDraftsFromGenerator(template: {
  holdTimeSec: number;
  maxLoadLbs: number;
  incrementLoadLbs: number;
}): WizardPlanStepDraft[] {
  const hold = Number(template.holdTimeSec);
  const max = Number(template.maxLoadLbs);
  const inc = Number(template.incrementLoadLbs);
  const rows: WizardPlanStepDraft[] = [{ holdTimeSec: hold, targetLoadLbs: 0 }];
  if (!Number.isFinite(max) || max <= 0 || !Number.isFinite(inc) || inc <= 0) {
    rows.push({ holdTimeSec: hold, targetLoadLbs: 0 });
    return rows;
  }
  for (let l = inc; l <= max; l += inc) {
    const v = Math.round(l * 1000) / 1000;
    if (rows[rows.length - 1]!.targetLoadLbs !== v) {
      rows.push({ holdTimeSec: hold, targetLoadLbs: v });
    }
  }
  if (rows[rows.length - 1]!.targetLoadLbs !== max) {
    rows.push({ holdTimeSec: hold, targetLoadLbs: max });
  }
  rows.push({ holdTimeSec: hold, targetLoadLbs: 0 });
  return rows;
}

/** Replace `customSteps` using current numeric fields (mock “Generate table”). */
export function rebuildCustomStepsFromNumbers(template: PullTestingWizardTestTemplate): void {
  template.customSteps = wizardStepDraftsFromGenerator(template);
}

export function rebuildNamedModelStepsFromNumbers(model: PullTestingWizardNamedModel): void {
  model.customSteps = wizardStepDraftsFromGenerator(model);
}

/** Middle rows only (first = baseline 0, last = unload 0). */
export function canDeleteScheduleRow(rows: WizardPlanStepDraft[] | null | undefined, index: number): boolean {
  const n = rows?.length ?? 0;
  return n > 2 && index > 0 && index < n - 1;
}

export function moveScheduleRow(rows: WizardPlanStepDraft[], fromIndex: number, toIndex: number): void {
  if (fromIndex === toIndex) return;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= rows.length || toIndex >= rows.length) return;
  const [row] = rows.splice(fromIndex, 1);
  rows.splice(toIndex, 0, row);
}

/**
 * Legacy hook: schedules now live on `template.models[].customSteps` once models exist.
 * When there are no named models, we do not fabricate `template.customSteps` (wizard “Test models” add flow).
 */
export function ensureCustomStepsForTemplate(template: PullTestingWizardTestTemplate): void {
  if (!template.enabled) return;
  if ((template.models?.length ?? 0) > 0) return;
}

function copyModelToTemplate(model: PullTestingWizardNamedModel, template: PullTestingWizardTestTemplate): void {
  template.scheduleName = model.name;
  template.maxLoadLbs = model.maxLoadLbs;
  template.incrementLoadLbs = model.incrementLoadLbs;
  template.holdTimeSec = model.holdTimeSec;
  template.lateralLoadHeightAboveGradeFt = model.lateralLoadHeightAboveGradeFt;
  template.lateralGaugeHeightIn = model.lateralGaugeHeightIn;
  template.deflectionFailureCriteriaIn = model.deflectionFailureCriteriaIn ?? null;
  template.lateralSecondDeflectionHeightFt =
    model.lateralSecondDeflectionHeightFt !== undefined ? model.lateralSecondDeflectionHeightFt : null;
  template.deflectionCount = deriveLateralDeflectionCount(template.lateralSecondDeflectionHeightFt);
  template.customSteps = model.customSteps ?? null;
}

function copyTemplateToModel(template: PullTestingWizardTestTemplate, model: PullTestingWizardNamedModel): void {
  model.name = template.scheduleName?.trim() || model.name;
  model.maxLoadLbs = template.maxLoadLbs;
  model.incrementLoadLbs = template.incrementLoadLbs;
  model.holdTimeSec = template.holdTimeSec;
  model.lateralLoadHeightAboveGradeFt = template.lateralLoadHeightAboveGradeFt;
  model.lateralGaugeHeightIn = template.lateralGaugeHeightIn;
  model.deflectionFailureCriteriaIn = template.deflectionFailureCriteriaIn ?? null;
  model.lateralSecondDeflectionHeightFt =
    template.lateralSecondDeflectionHeightFt !== undefined ? template.lateralSecondDeflectionHeightFt : null;
  model.deflectionCount = deriveLateralDeflectionCount(model.lateralSecondDeflectionHeightFt);
  model.customSteps = template.customSteps ?? null;
}

export function ensureModelsForTemplate(
  template: PullTestingWizardTestTemplate,
  defaultName: string
): PullTestingWizardNamedModel[] {
  if (!template.enabled && (!template.models || template.models.length === 0)) {
    return [];
  }
  if (!template.models || template.models.length === 0) {
    const m = defaultWizardNamedModel(defaultName);
    copyTemplateToModel(template, m);
    if (!m.customSteps || m.customSteps.length === 0) rebuildNamedModelStepsFromNumbers(m);
    template.models = [m];
    template.activeModelId = m.id;
  }
  const rows = template.models;
  let active = rows.find(m => m.id === template.activeModelId) ?? rows[0];
  if (!active.customSteps || active.customSteps.length === 0) rebuildNamedModelStepsFromNumbers(active);
  template.activeModelId = active.id;
  copyModelToTemplate(active, template);
  return rows;
}

export function selectTemplateModel(template: PullTestingWizardTestTemplate, modelId: string): void {
  const rows = template.models;
  if (!rows?.length) return;
  const model = rows.find(m => m.id === modelId);
  if (!model) return;
  template.activeModelId = model.id;
  copyModelToTemplate(model, template);
}

export function saveTemplateToActiveModel(template: PullTestingWizardTestTemplate): void {
  const rows = template.models;
  if (!rows?.length) return;
  const model = rows.find(m => m.id === template.activeModelId) ?? rows[0];
  copyTemplateToModel(template, model);
}

export function addTemplateModel(template: PullTestingWizardTestTemplate, name: string): PullTestingWizardNamedModel {
  const rows = template.models ?? [];
  template.enabled = true;
  const model = defaultWizardNamedModel(name);
  model.maxLoadLbs = template.maxLoadLbs;
  model.incrementLoadLbs = template.incrementLoadLbs;
  model.holdTimeSec = template.holdTimeSec;
  model.lateralLoadHeightAboveGradeFt = template.lateralLoadHeightAboveGradeFt;
  model.lateralGaugeHeightIn = template.lateralGaugeHeightIn;
  model.deflectionFailureCriteriaIn = template.deflectionFailureCriteriaIn ?? null;
  model.lateralSecondDeflectionHeightFt =
    template.lateralSecondDeflectionHeightFt !== undefined ? template.lateralSecondDeflectionHeightFt : null;
  model.deflectionCount = deriveLateralDeflectionCount(model.lateralSecondDeflectionHeightFt);
  rebuildNamedModelStepsFromNumbers(model);
  rows.push(model);
  template.models = rows;
  template.activeModelId = model.id;
  copyModelToTemplate(model, template);
  return model;
}

export function deleteTemplateModel(template: PullTestingWizardTestTemplate, modelId: string): void {
  const rows = template.models;
  if (!rows?.length) return;
  const idx = rows.findIndex(m => m.id === modelId);
  if (idx < 0) return;
  if (rows.length === 1) {
    template.models = [];
    template.activeModelId = null;
    template.customSteps = null;
    template.scheduleName = '';
    template.enabled = false;
    return;
  }
  rows.splice(idx, 1);
  const fallback = rows[Math.max(0, idx - 1)] ?? rows[0];
  template.activeModelId = fallback.id;
  copyModelToTemplate(fallback, template);
}

/** Build ascending load schedule in lbs: 0, increments…, max, 0 (unload). */
export function generateLoadScheduleLbs(maxLoadLbs: number, incrementLoadLbs: number): number[] {
  const loads: number[] = [0];
  const max = Number(maxLoadLbs);
  const inc = Number(incrementLoadLbs);
  if (!Number.isFinite(max) || max <= 0 || !Number.isFinite(inc) || inc <= 0) {
    return [0, Math.max(0, max), 0];
  }
  for (let v = inc; v < max; v += inc) {
    loads.push(Math.round(v * 1000) / 1000);
  }
  if (loads[loads.length - 1] !== max) {
    loads.push(max);
  }
  loads.push(0);
  return loads;
}

function buildPlanFromDrafts(
  drafts: WizardPlanStepDraft[],
  isLateral: boolean,
  lateralSecondHeightFt: number | null | undefined
): PullTestPlan {
  const steps: PullTestPlanStep[] = drafts.map((row, order) => ({
    order,
    targetLoadLbs: Number(row.targetLoadLbs),
    holdTimeSec: Number(row.holdTimeSec),
  }));
  const plan: PullTestPlan = { steps };
  if (isLateral) {
    const h = lateralSecondHeightFt;
    const normalized =
      h === undefined || h === null || (typeof h === 'number' && !Number.isFinite(h)) ? null : Number(h);
    plan.lateralSecondDeflectionHeightFt = normalized;
  }
  return plan;
}

function attachDeflectionFailureCriteria(plan: PullTestPlan, value: unknown): void {
  if (value === null || value === undefined) return;
  const n = Number(value);
  if (Number.isFinite(n) && n >= 0) {
    plan.deflectionFailureCriteriaIn = n;
  }
}

export function buildPlanFromTemplate(
  template: PullTestingWizardTestTemplate,
  isLateral: boolean
): PullTestPlan {
  const drafts: WizardPlanStepDraft[] =
    template.customSteps && template.customSteps.length > 0
      ? template.customSteps
      : wizardStepDraftsFromGenerator(template);
  const plan = buildPlanFromDrafts(drafts, isLateral, template.lateralSecondDeflectionHeightFt);
  attachDeflectionFailureCriteria(plan, template.deflectionFailureCriteriaIn);
  return plan;
}

/** Build plan from a named model card (Review assigns per-piling model). */
export function buildPlanFromNamedModel(model: PullTestingWizardNamedModel, isLateral: boolean): PullTestPlan {
  const drafts: WizardPlanStepDraft[] =
    model.customSteps && model.customSteps.length > 0 ? model.customSteps : wizardStepDraftsFromGenerator(model);
  const plan = buildPlanFromDrafts(drafts, isLateral, model.lateralSecondDeflectionHeightFt);
  attachDeflectionFailureCriteria(plan, model.deflectionFailureCriteriaIn);
  return plan;
}

export function findNamedModelInTemplate(
  template: PullTestingWizardTestTemplate,
  modelId: string | null | undefined
): PullTestingWizardNamedModel | null {
  const id = typeof modelId === 'string' ? modelId.trim() : '';
  if (!id) return null;
  return template.models?.find(m => m.id === id) ?? null;
}

function emptyResultsSteps(plan: PullTestPlan, deflectionCount: 2 | 3): PullTestResultsStep[] {
  return plan.steps.map(s => {
    const row: PullTestResultsStep = {
      order: s.order,
      appliedLoadLbs: null,
      deflection1In: null,
      deflection2In: null,
      comments: '',
    };
    if (deflectionCount === 3) {
      row.deflection3In = null;
    }
    return row;
  });
}

/**
 * Regenerate one row per **slot** (pile A, B, C … at a single site) — the defaults for **every** location.
 * `locationIndex` is always `0` (template only); `buildBulkPilingRows` multiplies by `numLocations` for the API.
 */
export function regenerateLoadCases(draft: {
  locationSerialStart: number;
  numLocations: number;
  pilingsPerLocation: number;
}): PullTestingWizardLoadCaseRow[] {
  const rows: PullTestingWizardLoadCaseRow[] = [];
  for (let slot = 0; slot < draft.pilingsPerLocation; slot++) {
    const id = slot < 26 ? String.fromCharCode(65 + slot) : `S${slot + 1}`;
    rows.push({
      locationIndex: 0,
      slotIndex: slot,
      pileIdentifier: id,
      pileLengthFt: null,
      embedmentDepthFt: null,
      soakTimeHours: null,
      pileType: 'H',
      pileCoating: '',
      pileSection: '',
      preExcavDepthFt: null,
      preDrillDiameterIn: null,
      preDrillDepthFt: null,
      enableTensile: true,
      enableLateral: false,
      enableCompression: false,
      tensileModelId: null,
      lateralModelId: null,
      compressionModelId: null,
    });
  }
  return rows;
}

/**
 * Apply the same tensile / lateral / compression toggles to every load case with this slot index
 * (with template-only load cases, typically one row per `slotIndex`).
 */
export function applyLoadCaseTestEnablesForSlot(
  loadCases: PullTestingWizardLoadCaseRow[],
  slotIndex: number,
  enableTensile: boolean,
  enableLateral: boolean,
  enableCompression: boolean
): void {
  for (const lc of loadCases) {
    if (lc.slotIndex !== slotIndex) continue;
    lc.enableTensile = enableTensile;
    lc.enableLateral = enableLateral;
    lc.enableCompression = enableCompression;
  }
}

export function buildCreatePullTestingModuleRequest(draft: PullTestingWizardDraft): CreatePullTestingModuleRequest {
  const installation = normalizePullTestingInstallationFields(draft.installation.map(f => ({ ...f })));
  return {
    pilePrefix: draft.pilePrefix.trim(),
    locationSerialStart: draft.locationSerialStart,
    numLocations: draft.numLocations,
    pilingsPerLocation: draft.pilingsPerLocation,
    installation,
  };
}

export function pullTestingWizardPilingOverrideKey(locationIndex: number, slotIndex: number): string {
  return `${locationIndex}:${slotIndex}`;
}

/** Pile id suffix (e.g. `A` in `PT-1A`) for bulk create / validation. */
export function effectiveWizardPileIdentifierSuffix(
  lc: PullTestingWizardLoadCaseRow,
  override?: PullTestingWizardPilingOverride | null
): string {
  const fromOv = override?.pileIdentifier?.trim();
  if (fromOv) return fromOv;
  return lc.pileIdentifier.trim();
}

/** Embedment (ft) after applying Review overrides. */
export function effectiveWizardEmbedmentDepthFt(
  lc: PullTestingWizardLoadCaseRow,
  override?: PullTestingWizardPilingOverride | null
): number | null {
  if (override?.embedmentDepthFt !== null && override?.embedmentDepthFt !== undefined) {
    const n = Number(override.embedmentDepthFt);
    if (Number.isFinite(n)) return n;
  }
  const b = lc.embedmentDepthFt;
  if (b === null || b === undefined) return null;
  const bn = Number(b);
  return Number.isFinite(bn) ? bn : null;
}

/** Pre-drill diameter (in) after applying Review overrides. */
export function effectiveWizardPreDrillDiameterIn(
  lc: PullTestingWizardLoadCaseRow,
  override?: PullTestingWizardPilingOverride | null
): number | null {
  if (override?.preDrillDiameterIn !== null && override?.preDrillDiameterIn !== undefined) {
    const n = Number(override.preDrillDiameterIn);
    if (Number.isFinite(n)) return n;
  }
  const b = lc.preDrillDiameterIn;
  if (b === null || b === undefined) return null;
  const bn = Number(b);
  return Number.isFinite(bn) ? bn : null;
}

/** Pre-drill depth (ft) after applying Review overrides. */
export function effectiveWizardPreDrillDepthFt(
  lc: PullTestingWizardLoadCaseRow,
  override?: PullTestingWizardPilingOverride | null
): number | null {
  if (override?.preDrillDepthFt !== null && override?.preDrillDepthFt !== undefined) {
    const n = Number(override.preDrillDepthFt);
    if (Number.isFinite(n)) return n;
  }
  const b = lc.preDrillDepthFt;
  if (b === null || b === undefined) return null;
  const bn = Number(b);
  return Number.isFinite(bn) ? bn : null;
}

/**
 * Above-grade pile height (ft) = pile length − effective embedment depth.
 * Returns `null` when either input is missing/invalid so callers can skip dependent checks.
 * Stored implicitly (not on the wizard draft) — every consumer recomputes from the load-case + override.
 */
export function wizardAboveGroundHeightFt(
  lc: PullTestingWizardLoadCaseRow,
  override?: PullTestingWizardPilingOverride | null
): number | null {
  const lengthRaw = lc.pileLengthFt;
  if (lengthRaw === null || lengthRaw === undefined || lengthRaw === ('' as unknown as number)) return null;
  const length = Number(lengthRaw);
  if (!Number.isFinite(length) || length <= 0) return null;
  const embed = effectiveWizardEmbedmentDepthFt(lc, override);
  if (embed === null || !Number.isFinite(embed) || embed < 0) return null;
  const above = length - embed;
  return Number.isFinite(above) ? above : null;
}

/**
 * Reasons a lateral test model can't be performed on a piling because the model's
 * physical heights exceed the pile's available above-grade portion.
 */
export interface WizardLateralModelHeightConflict {
  /** Available above-grade height (ft) for the piling under review. */
  aboveGroundHeightFt: number;
  /** Model load height above grade exceeds the available height. */
  loadHeightConflict: boolean;
  /** Numeric load height (ft) when {@link loadHeightConflict} is true. */
  loadHeightFt: number | null;
  /** Model 2nd deflection height exceeds the available height. */
  secondDeflectionConflict: boolean;
  /** Numeric 2nd deflection height (ft) when {@link secondDeflectionConflict} is true. */
  secondDeflectionHeightFt: number | null;
}

/**
 * Compare a lateral model's required heights against the piling's above-grade height.
 * Returns `null` when nothing conflicts (or when the inputs aren't comparable).
 */
export function wizardLateralModelHeightConflict(
  model: PullTestingWizardNamedModel | null | undefined,
  aboveGroundHeightFt: number | null | undefined
): WizardLateralModelHeightConflict | null {
  if (!model) return null;
  if (aboveGroundHeightFt === null || aboveGroundHeightFt === undefined) return null;
  const above = Number(aboveGroundHeightFt);
  if (!Number.isFinite(above) || above < 0) return null;

  const loadHeightRaw = model.lateralLoadHeightAboveGradeFt;
  const loadHeight = loadHeightRaw === null || loadHeightRaw === undefined ? NaN : Number(loadHeightRaw);
  const loadHeightConflict = Number.isFinite(loadHeight) && loadHeight > above;

  const usesSecondDeflection = (model.deflectionCount ?? 2) === 3;
  const secondHeightRaw = model.lateralSecondDeflectionHeightFt;
  const secondHeight = secondHeightRaw === null || secondHeightRaw === undefined ? NaN : Number(secondHeightRaw);
  const secondDeflectionConflict = usesSecondDeflection && Number.isFinite(secondHeight) && secondHeight > above;

  if (!loadHeightConflict && !secondDeflectionConflict) return null;
  return {
    aboveGroundHeightFt: above,
    loadHeightConflict,
    loadHeightFt: loadHeightConflict ? loadHeight : null,
    secondDeflectionConflict,
    secondDeflectionHeightFt: secondDeflectionConflict ? secondHeight : null,
  };
}

/** Format a feet value used in inline error helper text (e.g. `4.5 ft`). Trims trailing zeros. */
function formatFeetForError(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return `${rounded} ft`;
}

/** User-facing helper-text reason for a lateral height conflict (or `null` when none). */
export function wizardLateralModelHeightConflictMessage(
  conflict: WizardLateralModelHeightConflict | null
): string | null {
  if (!conflict) return null;
  const above = formatFeetForError(conflict.aboveGroundHeightFt);
  if (conflict.loadHeightConflict && conflict.secondDeflectionConflict) {
    const lh = conflict.loadHeightFt !== null ? formatFeetForError(conflict.loadHeightFt) : 'load height';
    const sh = conflict.secondDeflectionHeightFt !== null ? formatFeetForError(conflict.secondDeflectionHeightFt) : '2nd deflection height';
    return `Pile only stands ${above} above grade — model needs ${lh} for load + ${sh} for 2nd deflection.`;
  }
  if (conflict.loadHeightConflict) {
    const lh = conflict.loadHeightFt !== null ? formatFeetForError(conflict.loadHeightFt) : 'the load height';
    return `Load height (${lh}) exceeds the pile's above-grade height (${above}).`;
  }
  if (conflict.secondDeflectionConflict) {
    const sh = conflict.secondDeflectionHeightFt !== null ? formatFeetForError(conflict.secondDeflectionHeightFt) : 'the 2nd deflection height';
    return `2nd deflection height (${sh}) exceeds the pile's above-grade height (${above}).`;
  }
  return null;
}

/**
 * Canonical embedment text for wizard number inputs (`"04"` → `"4"`). Empty clears.
 * Preserves lone `-` and a trailing `.` while the user may still be typing a decimal.
 */
export function normalizeWizardEmbedmentInputDisplay(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (s === '') return '';
  if (s === '-') return '-';
  if (/\.$/.test(s)) return s;
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return String(n);
}

/**
 * Optional numeric columns on bulk piling rows: only serialize when the user set a value.
 * (`Number(null)` is `0`, which must not become a faux “pre-drill” reading.)
 */
function bulkOptionalNumeric(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n)) return undefined;
  return n;
}

/**
 * Build `pilings` array for `POST .../pull-testing/pilings/bulk`.
 * Omits `pullTestPilingID` / `projectID` — server assigns canonical ids.
 */
export function buildBulkPilingRows(
  draft: PullTestingWizardDraft,
  perPilingOverrides?: Record<string, PullTestingWizardPilingOverride | undefined>,
  /** Optional Review-step rows the user removed — omitted from bulk create. */
  excludedPilingKeys?: ReadonlySet<string>
): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  for (let loc = 0; loc < draft.numLocations; loc++) {
    for (const lc of draft.loadCases) {
      const rowKey = pullTestingWizardPilingOverrideKey(loc, lc.slotIndex);
      if (excludedPilingKeys?.has(rowKey)) continue;
      const ov = perPilingOverrides?.[rowKey];
      const tensileModelId = ov?.tensileModelId ?? lc.tensileModelId;
      const lateralModelId = ov?.lateralModelId ?? lc.lateralModelId;
      const compressionModelId = ov?.compressionModelId ?? lc.compressionModelId;
      const locationSerial = draft.locationSerialStart + loc;
      const useTensile = lc.enableTensile && draft.tensile.enabled;
      const useLateral = lc.enableLateral && draft.lateral.enabled;
      const useCompression = lc.enableCompression && draft.compression.enabled;

      const tensileModel = useTensile ? findNamedModelInTemplate(draft.tensile, tensileModelId) : null;
      const lateralModel = useLateral ? findNamedModelInTemplate(draft.lateral, lateralModelId) : null;
      const compressionModel = useCompression ? findNamedModelInTemplate(draft.compression, compressionModelId) : null;

      const tensilePlan =
        useTensile && tensileModel
          ? buildPlanFromNamedModel(tensileModel, false)
          : useTensile
            ? buildPlanFromTemplate(draft.tensile, false)
            : null;
      const lateralPlan =
        useLateral && lateralModel
          ? buildPlanFromNamedModel(lateralModel, true)
          : useLateral
            ? buildPlanFromTemplate(draft.lateral, true)
            : null;
      const compressionPlan =
        useCompression && compressionModel
          ? buildPlanFromNamedModel(compressionModel, false)
          : useCompression
            ? buildPlanFromTemplate(draft.compression, false)
            : null;

      const row: Record<string, unknown> = {
        locationSerial,
        slotIndex: lc.slotIndex,
        pileIdentifier: effectiveWizardPileIdentifierSuffix(lc, ov),
        embedmentDepthFt: effectiveWizardEmbedmentDepthFt(lc, ov) as number,
        soakTimeHours: Number(lc.soakTimeHours),
        status: 'awaiting installation',
      };
      if (lc.pileType.trim()) {
        row['pileType'] = lc.pileType.trim();
      }
      if (Number.isFinite(Number(lc.pileLengthFt))) row['pileLengthFt'] = Number(lc.pileLengthFt);
      if (lc.pileCoating?.trim()) row['pileCoating'] = lc.pileCoating.trim();
      if (lc.pileSection?.trim()) row['pileSection'] = lc.pileSection.trim();
      const preExcav = bulkOptionalNumeric(lc.preExcavDepthFt);
      if (preExcav !== undefined) row['preExcavDepthFt'] = preExcav;
      const preDrillDiam = bulkOptionalNumeric(effectiveWizardPreDrillDiameterIn(lc, ov));
      if (preDrillDiam !== undefined) row['preDrillDiameterIn'] = preDrillDiam;
      const preDrillDepth = bulkOptionalNumeric(effectiveWizardPreDrillDepthFt(lc, ov));
      if (preDrillDepth !== undefined) row['preDrillDepthFt'] = preDrillDepth;

      if (tensilePlan) {
        row['tensileTestPlan'] = tensilePlan;
        row['tensileTestResults'] = {
          status: 'not_started',
          steps: emptyResultsSteps(tensilePlan, 2),
        };
      } else {
        row['tensileTestPlan'] = null;
        row['tensileTestResults'] = null;
      }

      if (lateralPlan) {
        const lateralDefl = deriveLateralDeflectionCount(
          lateralModel?.lateralSecondDeflectionHeightFt ?? draft.lateral.lateralSecondDeflectionHeightFt
        );
        row['lateralTestPlan'] = lateralPlan;
        row['lateralTestResults'] = {
          status: 'not_started',
          loadHeightAboveGradeFt:
            lateralModel?.lateralLoadHeightAboveGradeFt ?? draft.lateral.lateralLoadHeightAboveGradeFt ?? null,
          gaugeHeightIn: lateralModel?.lateralGaugeHeightIn ?? draft.lateral.lateralGaugeHeightIn ?? null,
          steps: emptyResultsSteps(lateralPlan, lateralDefl),
        };
      } else {
        row['lateralTestPlan'] = null;
        row['lateralTestResults'] = null;
      }

      if (compressionPlan) {
        row['compressionTestPlan'] = compressionPlan;
        row['compressionTestResults'] = {
          status: 'not_started',
          steps: emptyResultsSteps(compressionPlan, 2),
        };
      } else {
        row['compressionTestPlan'] = null;
        row['compressionTestResults'] = null;
      }

      rows.push(row);
    }
  }
  return rows;
}

/** Chunk bulk rows for `POST .../pull-testing/pilings/bulk` (default {@link PULL_TEST_BULK_CREATE_MAX_ROWS}). */
export function chunkBulkPilings<T>(
  pilings: T[],
  chunkSize: number = PULL_TEST_BULK_CREATE_MAX_ROWS
): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < pilings.length; i += chunkSize) {
    out.push(pilings.slice(i, i + chunkSize));
  }
  return out;
}

export function createInitialWizardDraft(): PullTestingWizardDraft {
  const base: PullTestingWizardDraft = {
    pilePrefix: 'PT',
    locationSerialStart: 1,
    numLocations: 1,
    pilingsPerLocation: 1,
    installation: defaultPullTestingInstallationCapture(),
    tensile: { ...defaultWizardTestTemplate(), enabled: true },
    lateral: { ...defaultWizardTestTemplate(), enabled: false },
    compression: { ...defaultWizardTestTemplate(), enabled: false },
    loadCases: [],
  };
  base.loadCases = regenerateLoadCases(base);
  return base;
}

/**
 * Lateral deflection count is derived from whether a 2nd deflection height is set:
 * - Height present (a positive finite number) → 3 readings (deflection 1, 2, and 3).
 * - Height missing/zero/invalid → 2 readings (deflection 1 and 2), same as tensile/compression.
 *
 * The wizard UI no longer exposes a separate "readings per step" field; this derivation is
 * the single source of truth for the count and is applied during normalization.
 */
export function deriveLateralDeflectionCount(secondDeflectionHeightFt: unknown): 2 | 3 {
  if (secondDeflectionHeightFt === null || secondDeflectionHeightFt === undefined) return 2;
  const n = Number(secondDeflectionHeightFt);
  if (!Number.isFinite(n) || n <= 0) return 2;
  return 3;
}

/** Normalize numeric template fields (e.g. after `ion-input` string binding). */
export function normalizePullTestingWizardDraft(d: PullTestingWizardDraft): void {
  d.tensile.deflectionCount = 2;
  d.compression.deflectionCount = 2;
  d.lateral.deflectionCount = deriveLateralDeflectionCount(d.lateral.lateralSecondDeflectionHeightFt);
  for (const m of d.tensile.models ?? []) m.deflectionCount = 2;
  for (const m of d.compression.models ?? []) m.deflectionCount = 2;
  for (const m of d.lateral.models ?? []) m.deflectionCount = deriveLateralDeflectionCount(m.lateralSecondDeflectionHeightFt);
  d.locationSerialStart = Math.max(1, Math.round(Number(d.locationSerialStart)) || 1);
  d.numLocations = Math.max(1, Math.round(Number(d.numLocations)) || 1);
  d.pilingsPerLocation = Math.min(10, Math.max(1, Math.round(Number(d.pilingsPerLocation)) || 1));
  ensureCustomStepsForTemplate(d.tensile);
  ensureCustomStepsForTemplate(d.lateral);
  ensureCustomStepsForTemplate(d.compression);
}

/**
 * Single user-facing message when load-case (UI step 4) validation fails — avoids a long stacked banner.
 */
export const PULL_TEST_WIZARD_LOAD_CASE_STEP_ERROR_SUMMARY =
  'Some load case fields are invalid or incomplete. Please review the form and try again.';

/**
 * Single user-facing message when Review (UI step 5) validation fails on Submit — keeps the banner
 * short and consistent with steps 3/4. Specific fields are already highlighted inline.
 */
export const PULL_TEST_WIZARD_REVIEW_STEP_ERROR_SUMMARY =
  'Some piling assignments are invalid or incomplete. Please review the highlighted fields and try again.';

/**
 * Validates load-case (step 4 in the UI) pile properties and test toggles only — use when leaving that step.
 */
export function validatePullTestingWizardLoadCaseStep(d: PullTestingWizardDraft): string[] {
  normalizePullTestingWizardDraft(d);
  const err: string[] = [];
  pushLoadCaseTemplateValidationErrors(d, err);
  return err;
}

/** Load-case template step: keys for red outlines (`lc:slotIndex#kind`). */
export type PullTestingWizardLoadCaseTemplateHighlightKind =
  | 'pileIdentifier'
  | 'pileLengthFt'
  | 'pileType'
  | 'embedmentDepthFt'
  | 'soakTimeHours'
  | 'pileSection'
  | 'anyTestType';

export function pullTestingWizardLoadCaseFieldKey(
  slotIndex: number,
  kind: PullTestingWizardLoadCaseTemplateHighlightKind
): string {
  return `lc:${slotIndex}#${kind}`;
}

/**
 * Load-case rows that fail the same checks as `validatePullTestingWizardLoadCaseStep` — for inline `field-invalid`.
 */
export function pullTestingWizardLoadCaseInvalidFieldKeys(d: PullTestingWizardDraft): ReadonlySet<string> {
  normalizePullTestingWizardDraft(d);
  const keys = new Set<string>();
  const err: string[] = [];
  pushLoadCaseTemplateValidationErrors(d, err, keys);
  return keys;
}

function pushLoadCaseTemplateValidationErrors(
  d: PullTestingWizardDraft,
  err: string[],
  fieldKeys?: Set<string>
): void {
  const mark = (slotIndex: number, kind: PullTestingWizardLoadCaseTemplateHighlightKind) => {
    fieldKeys?.add(pullTestingWizardLoadCaseFieldKey(slotIndex, kind));
  };

  if (d.loadCases.length === 0) {
    err.push('No load cases — check location counts.');
    return;
  }
  for (let i = 0; i < d.loadCases.length; i++) {
    const lc = d.loadCases[i];
    const rowNum = i + 1;
    const si = lc.slotIndex;
    if (!lc.pileIdentifier.trim()) {
      err.push(`Load cases row ${rowNum}: pile identifier is required.`);
      mark(si, 'pileIdentifier');
    }
    if (!Number.isFinite(Number(lc.pileLengthFt)) || Number(lc.pileLengthFt) <= 0) {
      err.push(`Load cases row ${rowNum}: pile length (ft) is required.`);
      mark(si, 'pileLengthFt');
    }
    if (!lc.pileType?.trim()) {
      err.push(`Load cases row ${rowNum}: pile type is required.`);
      mark(si, 'pileType');
    }
    if (
      lc.embedmentDepthFt === null ||
      lc.embedmentDepthFt === undefined ||
      !Number.isFinite(Number(lc.embedmentDepthFt)) ||
      Number(lc.embedmentDepthFt) < 0
    ) {
      err.push(`Load cases row ${rowNum}: embedment (ft) is required.`);
      mark(si, 'embedmentDepthFt');
    }
    const lengthN = Number(lc.pileLengthFt);
    const embedN = Number(lc.embedmentDepthFt);
    if (
      Number.isFinite(lengthN) &&
      lengthN > 0 &&
      Number.isFinite(embedN) &&
      embedN >= 0 &&
      embedN >= lengthN
    ) {
      err.push(
        `Load cases row ${rowNum}: embedment (${embedN} ft) must be less than pile length (${lengthN} ft).`
      );
      mark(si, 'pileLengthFt');
      mark(si, 'embedmentDepthFt');
    }
    if (
      lc.soakTimeHours === null ||
      lc.soakTimeHours === undefined ||
      !Number.isFinite(Number(lc.soakTimeHours)) ||
      Number(lc.soakTimeHours) < 0
    ) {
      err.push(`Load cases row ${rowNum}: soak time (hours) is required.`);
      mark(si, 'soakTimeHours');
    }
    if (!lc.pileSection?.trim()) {
      err.push(`Load cases row ${rowNum}: pile section is required.`);
      mark(si, 'pileSection');
    }

    const rowAny = lc.enableTensile || lc.enableLateral || lc.enableCompression;
    if (!rowAny) {
      err.push(`Load cases row ${rowNum}: enable at least one test type.`);
      mark(si, 'anyTestType');
    }
  }
}

/** Keys for highlighting Review-step cells (`${locationIndex}:${slotIndex}#${kind}`). */
export type PullTestingWizardReviewHighlightKind =
  | 'suffix'
  | 'embedment'
  | 'tensileModel'
  | 'lateralModel'
  | 'compressionModel';

export function pullTestingWizardReviewFieldKey(
  locationIndex: number,
  slotIndex: number,
  kind: PullTestingWizardReviewHighlightKind
): string {
  return `${pullTestingWizardPilingOverrideKey(locationIndex, slotIndex)}#${kind}`;
}

/**
 * Review-step fields that violate the same rules as `pushLoadCaseReviewValidationErrors`
 * (excluding message text). Consumers show `field-invalid` / `aria-invalid` on matching inputs.
 */
export function pullTestingWizardReviewInvalidFieldKeys(
  d: PullTestingWizardDraft,
  perPilingOverrides?: Record<string, PullTestingWizardPilingOverride | undefined>,
  excludedPilingKeys?: ReadonlySet<string>
): ReadonlySet<string> {
  normalizePullTestingWizardDraft(d);
  const keys = new Set<string>();
  const tensileModels = d.tensile.models?.length ?? 0;
  const lateralModels = d.lateral.models?.length ?? 0;
  const compressionModels = d.compression.models?.length ?? 0;

  for (let loc = 0; loc < d.numLocations; loc++) {
    for (const lc of d.loadCases) {
      const rowKey = pullTestingWizardPilingOverrideKey(loc, lc.slotIndex);
      if (excludedPilingKeys?.has(rowKey)) continue;
      const ov = perPilingOverrides?.[rowKey];
      const suffix = effectiveWizardPileIdentifierSuffix(lc, ov);
      if (!suffix) {
        keys.add(pullTestingWizardReviewFieldKey(loc, lc.slotIndex, 'suffix'));
      }
      const emb = effectiveWizardEmbedmentDepthFt(lc, ov);
      if (emb === null || !Number.isFinite(emb) || emb < 0) {
        keys.add(pullTestingWizardReviewFieldKey(loc, lc.slotIndex, 'embedment'));
      } else {
        // Override may push embedment past the (template) pile length — flag the cell so the row goes red
        // (same logical contradiction as on the Load cases step, but only when overridden here).
        const lengthN = Number(lc.pileLengthFt);
        if (Number.isFinite(lengthN) && lengthN > 0 && emb >= lengthN) {
          keys.add(pullTestingWizardReviewFieldKey(loc, lc.slotIndex, 'embedment'));
        }
      }

      const tensileModelId = ov?.tensileModelId ?? lc.tensileModelId;
      const lateralModelId = ov?.lateralModelId ?? lc.lateralModelId;
      const compressionModelId = ov?.compressionModelId ?? lc.compressionModelId;

      if (lc.enableTensile && d.tensile.enabled && tensileModels > 0 && !findNamedModelInTemplate(d.tensile, tensileModelId)) {
        keys.add(pullTestingWizardReviewFieldKey(loc, lc.slotIndex, 'tensileModel'));
      }
      if (lc.enableLateral && d.lateral.enabled && lateralModels > 0) {
        const lateralModel = findNamedModelInTemplate(d.lateral, lateralModelId);
        if (!lateralModel) {
          keys.add(pullTestingWizardReviewFieldKey(loc, lc.slotIndex, 'lateralModel'));
        } else {
          // Lateral test heights must fit on the available above-grade portion of the piling.
          const above = wizardAboveGroundHeightFt(lc, ov);
          if (wizardLateralModelHeightConflict(lateralModel, above)) {
            keys.add(pullTestingWizardReviewFieldKey(loc, lc.slotIndex, 'lateralModel'));
          }
        }
      }
      if (
        lc.enableCompression &&
        d.compression.enabled &&
        compressionModels > 0 &&
        !findNamedModelInTemplate(d.compression, compressionModelId)
      ) {
        keys.add(pullTestingWizardReviewFieldKey(loc, lc.slotIndex, 'compressionModel'));
      }
    }
  }
  return keys;
}

function pushLoadCaseReviewValidationErrors(
  d: PullTestingWizardDraft,
  perPilingOverrides: Record<string, PullTestingWizardPilingOverride | undefined> | undefined,
  err: string[],
  excludedPilingKeys?: ReadonlySet<string>
): void {
  const tensileModels = d.tensile.models?.length ?? 0;
  const lateralModels = d.lateral.models?.length ?? 0;
  const compressionModels = d.compression.models?.length ?? 0;

  for (let loc = 0; loc < d.numLocations; loc++) {
    const serial = d.locationSerialStart + loc;
    for (let i = 0; i < d.loadCases.length; i++) {
      const lc = d.loadCases[i];
      const key = pullTestingWizardPilingOverrideKey(loc, lc.slotIndex);
      if (excludedPilingKeys?.has(key)) continue;
      const ov = perPilingOverrides?.[key];
      const suffix = effectiveWizardPileIdentifierSuffix(lc, ov);
      if (!suffix) {
        err.push(`Location ${serial}, pile slot ${i + 1}: piling ID suffix is required.`);
      }
      const emb = effectiveWizardEmbedmentDepthFt(lc, ov);
      if (emb === null || !Number.isFinite(emb) || emb < 0) {
        err.push(`Location ${serial}, pile slot ${i + 1}: embedment (ft) is required.`);
      } else {
        const lengthN = Number(lc.pileLengthFt);
        if (Number.isFinite(lengthN) && lengthN > 0 && emb >= lengthN) {
          err.push(
            `Location ${serial}, pile slot ${i + 1}: embedment (${emb} ft) must be less than pile length (${lengthN} ft).`
          );
        }
      }

      const tensileModelId = ov?.tensileModelId ?? lc.tensileModelId;
      const lateralModelId = ov?.lateralModelId ?? lc.lateralModelId;
      const compressionModelId = ov?.compressionModelId ?? lc.compressionModelId;

      if (lc.enableTensile && d.tensile.enabled && tensileModels > 0 && !findNamedModelInTemplate(d.tensile, tensileModelId)) {
        err.push(`Location ${serial}, pile slot ${i + 1}: assign a tensile (uplift) model.`);
      }
      if (lc.enableLateral && d.lateral.enabled && lateralModels > 0) {
        const lateralModel = findNamedModelInTemplate(d.lateral, lateralModelId);
        if (!lateralModel) {
          err.push(`Location ${serial}, pile slot ${i + 1}: assign a lateral model.`);
        } else {
          const above = wizardAboveGroundHeightFt(lc, ov);
          const conflict = wizardLateralModelHeightConflict(lateralModel, above);
          if (conflict) {
            const message = wizardLateralModelHeightConflictMessage(conflict);
            if (message) {
              err.push(`Location ${serial}, pile slot ${i + 1}: ${message}`);
            }
          }
        }
      }
      if (
        lc.enableCompression &&
        d.compression.enabled &&
        compressionModels > 0 &&
        !findNamedModelInTemplate(d.compression, compressionModelId)
      ) {
        err.push(`Location ${serial}, pile slot ${i + 1}: assign a compression model.`);
      }
    }
  }
}

/** Client-side validation before API calls (and optionally when leaving the load-cases step). */
export function validatePullTestingWizardDraft(
  d: PullTestingWizardDraft,
  perPilingOverrides?: Record<string, PullTestingWizardPilingOverride | undefined>,
  /** Review rows removed by the user — not validated and not sent in `buildBulkPilingRows`. */
  excludedPilingKeys?: ReadonlySet<string>
): string[] {
  normalizePullTestingWizardDraft(d);
  const err: string[] = [];
  const p = d.pilePrefix.trim();
  if (!p) err.push('Pile prefix is required.');
  if (p.length > 10) err.push('Pile prefix must be at most 10 characters.');
  if (!Number.isInteger(d.locationSerialStart) || d.locationSerialStart < 1) {
    err.push('Location serial start must be an integer ≥ 1.');
  }
  if (!Number.isInteger(d.numLocations) || d.numLocations < 1) {
    err.push('Number of locations must be an integer ≥ 1.');
  }
  if (!Number.isInteger(d.pilingsPerLocation) || d.pilingsPerLocation < 1 || d.pilingsPerLocation > 10) {
    err.push('Pilings per location must be between 1 and 10.');
  }
  if (!wizardLoadCasesInSync(d)) {
    err.push(
      'Load case rows are out of sync with pilings per location — return to Project Setup and continue forward through Load Cases again.'
    );
  }
  const anyTemplate = d.tensile.enabled || d.lateral.enabled || d.compression.enabled;
  if (!anyTemplate) err.push('Enable at least one test type in Test models.');
  const validateTypeSteps = (label: string, t: PullTestingWizardTestTemplate) => {
    if (!t.enabled) return;
    const models = t.models ?? [];
    if (models.length === 0) return;
    for (let mi = 0; mi < models.length; mi++) {
      const m = models[mi];
      const dfc = m.deflectionFailureCriteriaIn;
      if (dfc === null || dfc === undefined || !Number.isFinite(Number(dfc)) || Number(dfc) < 0) {
        err.push(`${label} (“${m.name}”): deflection failure criteria (inches) is required.`);
      }
      const steps = m.customSteps ?? [];
      const n = steps.length;
      if (n < 2) {
        err.push(`${label} (“${m.name}”): add at least two schedule steps (baseline and unload).`);
        continue;
      }
      for (let i = 0; i < n; i++) {
        const s = steps[i];
        if (!Number.isFinite(Number(s.holdTimeSec)) || Number(s.holdTimeSec) < 0) {
          err.push(`${label} (“${m.name}”): row ${i + 1} has invalid hold time.`);
        }
        if (!Number.isFinite(Number(s.targetLoadLbs))) {
          err.push(`${label} (“${m.name}”): row ${i + 1} has invalid target load.`);
        }
      }
    }
  };
  validateTypeSteps('Tensile', d.tensile);
  validateTypeSteps('Lateral', d.lateral);
  validateTypeSteps('Compression', d.compression);
  pushLoadCaseTemplateValidationErrors(d, err);
  pushLoadCaseReviewValidationErrors(d, perPilingOverrides, err, excludedPilingKeys);
  let activeReviewPilings = 0;
  for (let loc = 0; loc < d.numLocations; loc++) {
    for (const lc of d.loadCases) {
      const key = pullTestingWizardPilingOverrideKey(loc, lc.slotIndex);
      if (excludedPilingKeys?.has(key)) continue;
      activeReviewPilings++;
    }
  }
  if (activeReviewPilings < 1) {
    err.push('At least one piling must remain on the assignment table.');
  }
  return err;
}

/**
 * Next suggested location serial for “Create load set” / append wizard step 0.
 * Uses max existing `locationSerial` + 1; falls back to module `locationSerialStart` or 1.
 */
export function suggestNextPullTestLocationSerialStart(
  pilings: ReadonlyArray<Pick<PullTestPiling, 'locationSerial'>>,
  moduleLocationSerialStart?: number
): number {
  let max = 0;
  for (const p of pilings) {
    const n = p.locationSerial;
    if (typeof n === 'number' && Number.isFinite(n) && n > max) {
      max = n;
    }
  }
  if (max > 0) {
    return max + 1;
  }
  const fallback =
    typeof moduleLocationSerialStart === 'number' && Number.isFinite(moduleLocationSerialStart)
      ? moduleLocationSerialStart
      : 1;
  return Math.max(1, Math.round(fallback) || 1);
}

/** True when load-case template rows match the configured pilings-per-location count. */
export function wizardLoadCasesInSync(d: Pick<PullTestingWizardDraft, 'loadCases' | 'pilingsPerLocation'>): boolean {
  return d.loadCases.length === d.pilingsPerLocation;
}

/**
 * Pre-fill a wizard draft from a `pending` module so setup can resume after a partial bulk failure.
 * Regenerates load-case rows from module counts (test models remain user-defined in the wizard).
 */
export function applyPendingPullTestingModuleToWizardDraft(
  draft: PullTestingWizardDraft,
  module: Pick<
    PullTestingModule,
    'pilePrefix' | 'locationSerialStart' | 'numLocations' | 'pilingsPerLocation' | 'installation'
  >
): void {
  const prefix = String(module.pilePrefix ?? draft.pilePrefix).trim();
  if (prefix) {
    draft.pilePrefix = prefix;
  }
  if (typeof module.locationSerialStart === 'number' && Number.isFinite(module.locationSerialStart)) {
    draft.locationSerialStart = Math.max(1, Math.round(module.locationSerialStart));
  }
  if (typeof module.numLocations === 'number' && Number.isFinite(module.numLocations) && module.numLocations >= 1) {
    draft.numLocations = Math.max(1, Math.round(module.numLocations));
  }
  if (
    typeof module.pilingsPerLocation === 'number' &&
    Number.isFinite(module.pilingsPerLocation) &&
    module.pilingsPerLocation >= 1
  ) {
    draft.pilingsPerLocation = Math.min(10, Math.max(1, Math.round(module.pilingsPerLocation)));
  }
  draft.installation = resolvePullTestingInstallationFields(module.installation).map(f => ({ ...f }));
  draft.loadCases = regenerateLoadCases(draft);
}

/** Wizard draft for appending pilings to an existing Pull Testing module (empty test models). */
export function buildAppendPullTestingWizardDraft(
  module: Pick<PullTestingModule, 'pilePrefix' | 'locationSerialStart' | 'installation'>,
  existingPilings: ReadonlyArray<Pick<PullTestPiling, 'locationSerial'>>
): PullTestingWizardDraft {
  const prefix = String(module.pilePrefix ?? 'PT').trim() || 'PT';
  const installation = resolvePullTestingInstallationFields(module.installation);
  const draft = createInitialWizardDraft();
  draft.pilePrefix = prefix;
  draft.locationSerialStart = suggestNextPullTestLocationSerialStart(existingPilings, module.locationSerialStart);
  draft.numLocations = 1;
  draft.pilingsPerLocation = 1;
  draft.installation = installation.map(f => ({ ...f }));
  draft.tensile = defaultWizardTestTemplate();
  draft.lateral = defaultWizardTestTemplate();
  draft.compression = defaultWizardTestTemplate();
  draft.loadCases = regenerateLoadCases(draft);
  return draft;
}
